import { createSelector, createSlice } from "@reduxjs/toolkit";
import * as endorsementsResolvers from "domain/endorsementsResolvers";
import * as persistenceActionTypes from "store/actions/persistence/persistenceActionTypes";

const initialState = {};

const endorsementsSlice = createSlice({
  name: "endorsements",
  initialState,
  reducers: {
    set: (state, action) => {
      const values = action?.payload ?? {};
      if (typeof values !== "object") {
        return;
      }
      return {
        ...initialState,
        ...values,
      };
    },
    setLayerEndorsements: (state, action) => {
      const { layerId, endorsements, predicateMatch } = action.payload;
      if (!layerId) {
        return;
      }
      return {
        ...state,
        endorsementsByLayer: {
          ...state.endorsementsByLayer,
          [layerId]: endorsements,
        },
        predicateMatchByLayer: {
          ...state.predicateMatchByLayer,
          [layerId]: predicateMatch,
        },
      };
    },
    setLayerTermsAndConditions: (state, action) => {
      const layerId = action.payload.layerId;
      if (!layerId) {
        return;
      }
      return {
        ...state,
        termsAndConditionsByLayer: {
          ...state.termsAndConditionsByLayer,
          [layerId]: action.payload.termsAndConditions,
        },
      };
    },
    setLayerSubjectivities: (state, action) => {
      const layerId = action.payload.layerId;
      if (!layerId) {
        return;
      }
      return {
        ...state,
        subjectivitiesByLayer: {
          ...state.subjectivitiesByLayer,
          [layerId]: action.payload.subjectivities,
        },
      };
    },
    lockLayerSubjectivities: (state, action) => {
      const layerId = action.payload.layerId;
      if (!layerId) {
        return;
      }
      return {
        ...state,
        subjectivitiesLockedByLayer: {
          ...state.subjectivitiesLockedByLayer,
          [layerId]: true,
        },
      };
    },
    setLayerSpecialConditions: (state, action) => {
      const layerId = action.payload.layerId;
      if (!layerId) {
        return;
      }
      return {
        ...state,
        specialConditionsByLayer: {
          ...state.specialConditionsByLayer,
          [layerId]: action.payload.specialConditions,
        },
      };
    },
    update: (state, action) => {
      const values = action?.payload;
      if (typeof values !== "object") {
        return;
      }
      return {
        ...state,
        ...values,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      persistenceActionTypes.LOAD_SUBMISSION_SUCCEEDED,
      (_, action) => action?.payload?.data?.state?.endorsements ?? initialState
    );
  },
});

export const selectInput = createSelector(
  [(state) => state?.endorsements ?? null],
  (endorsements) => endorsements
);

export const select = createSelector([selectInput], (input) =>
  endorsementsResolvers.resolveEndorsements(input)
);

export const selectSubjectivitiesLockedByLayer = (layerId) =>
  createSelector(
    [selectInput],
    (input) => input?.subjectivitiesLockedByLayer?.[layerId] ?? false
  );

export const {
  set,
  setLayerEndorsements,
  setLayerTermsAndConditions,
  setLayerSubjectivities,
  lockLayerSubjectivities,
  setLayerSpecialConditions,
  update,
} = endorsementsSlice.actions;

export default endorsementsSlice;
